/**
 * AI School Summary Tool
 * 
 * Admin-only interface for generating comprehensive AI-powered school summaries.
 * Provides three tabs: Geographic, Competitive, and Projections analysis.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Summarize as SummarizeIcon,
  School as SchoolIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  Public as PublicIcon,
  DonutSmall as CompetitiveIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Room as RoomIcon,
  BarChart as BarChartIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import AssistantIcon from '@mui/icons-material/Assistant';
import PageLayout from '../../shared_components/pageLayout';
import FormattedNarrative from './FormattedNarrative';
import { adminService } from '../../../services/adminService';
import { 
  getGeographicSummary, 
  getCompetitiveSummary, 
  getProjectionsSummary,
  generateAllSummariesParallel,
  saveCurrentSummaries,
  getSummaryStatus,
  getStoredSummariesAdmin,
  getSummaryDataOnly,
  generateForTeamStream,
  generateForAllStream
} from '../../../services/aiService';
import { useSnackbar } from 'notistack';
import GeographicSummaryTab from './GeographicSummaryTab';
import CompetitiveSummaryTab from './CompetitiveSummaryTab';
import ProjectionsSummaryTab from './ProjectionsSummaryTab';
import QualityReportTab from './QualityReportTab';
import PromptEditor from './PromptEditor';
import ExportModal from './ExportModal';
import BatchProgressDialog from './BatchProgressDialog';
import SummaryOverview from './SummaryOverview';
import MultiSchoolSummaryTab from './multi_school/MultiSchoolSummaryTab';
import { exportSummaryToHTML } from '../../../utils/htmlExport';

const GRADES = ['Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const DRIVE_TIMES = [5, 10, 15];
const GOVERNANCE_OPTIONS = ['Charter', 'District', 'Private'];

const AISummary = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Top-level tab state (Generate & View vs Overview & Metrics)
  const [mainTab, setMainTab] = useState(0);
  
  // State for school selection
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamSchools, setTeamSchools] = useState([]);
  const [loadingTeamSchools, setLoadingTeamSchools] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  
  // State for parameters
  const [driveTime, setDriveTime] = useState(10);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [governanceFilter, setGovernanceFilter] = useState(['Charter', 'District', 'Private']);
  const [schoolTypeFilter] = useState([]); // eslint-disable-line no-unused-vars
  
  // AI configuration
  const [verbosity, setVerbosity] = useState('medium');
  const [reasoningEffort, setReasoningEffort] = useState('medium');
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  
  // State for tab management
  const [activeTab, setActiveTab] = useState(0);
  
  // State for summaries
  const [geographicSummary, setGeographicSummary] = useState(null);
  const [competitiveSummary, setCompetitiveSummary] = useState(null);
  const [projectionsSummary, setProjectionsSummary] = useState(null);
  const [combinedSummary, setCombinedSummary] = useState(null);
  
  // Loading states
  const [loadingGeographic, setLoadingGeographic] = useState(false);
  const [loadingCompetitive, setLoadingCompetitive] = useState(false);
  const [loadingProjections, setLoadingProjections] = useState(false);
  // Combined summary loading uses loadingStored since it's fetched together
  
  // Error states
  const [error, setError] = useState(null);
  
  // Prompt editor state
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  
  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Storage status
  const [summaryStatus, setSummaryStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generateAllDriveTimes, setGenerateAllDriveTimes] = useState(true);
  
  // Generation dates from stored summaries
  const [storedSummaryDates, setStoredSummaryDates] = useState({});
  const [loadingStored, setLoadingStored] = useState(false);
  
  // Batch generation state
  const [generatingForTeam, setGeneratingForTeam] = useState(false);
  const [generatingForAll, setGeneratingForAll] = useState(false);
  const [batchSize, setBatchSize] = useState(250);
  const [skipExistingForAll, setSkipExistingForAll] = useState(true);
  const [onlyStaleForAll, setOnlyStaleForAll] = useState(true);
  const [staleDaysForAll, setStaleDaysForAll] = useState(7);
  const [locationScopeForAll, setLocationScopeForAll] = useState('all');
  const [stopOnVerificationFailure, setStopOnVerificationFailure] = useState(true);
  
  // Progress tracking for parallel generation
  const [generationProgress, setGenerationProgress] = useState(null); // { phase, completedCount, totalCount }
  
  // Batch progress tracking for "Generate for All" and "Generate for Team"
  const [batchProgress, setBatchProgress] = useState({
    isActive: false,
    totalSchools: 0,
    completed: 0,
    failed: 0,
    verificationFailed: 0,
    currentBatch: 0,
    totalBatches: 0,
    startTime: null,
    avgTimePerSchool: 0,
    failedSchools: [],
    verificationFailedSchools: [],
    recentCompletions: [],
    mode: null // 'team' | 'all'
  });
  const [batchProgressDialogOpen, setBatchProgressDialogOpen] = useState(false);
  
  // Detect drop-a-pin location
  // Drop-a-pins have no NCES ID and no enrollment data
  // They can only have geographic summaries
  const isDropAPinLocation = useMemo(() => {
    if (!selectedSchool) return false;
    // Check if school has no NCES ID (simple detection)
    // A more robust check would also verify no enrollment data, but NCES ID is sufficient
    return !selectedSchool.ncessch;
  }, [selectedSchool]);
  
  // Load teams on mount
  useEffect(() => {
    const loadAllTeams = async () => {
      setLoadingTeams(true);
      try {
        const response = await adminService.listTeams(0, 1000, enqueueSnackbar, true);
        const teamsData = response?.teams || [];
        setTeams(teamsData.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (err) {
        console.error('Error loading teams:', err);
        enqueueSnackbar('Failed to load teams', { variant: 'error' });
      } finally {
        setLoadingTeams(false);
      }
    };
    
    loadAllTeams();
  }, [enqueueSnackbar]);
  
  // Load schools for selected team
  useEffect(() => {
    const loadSchoolsForTeam = async () => {
      if (!selectedTeam?.id) {
        setTeamSchools([]);
        return;
      }

      setLoadingTeamSchools(true);
      try {
        const response = await adminService.getTeamSchools(selectedTeam.id, enqueueSnackbar);
        const schools = response?.schools || [];
        setTeamSchools(schools.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (err) {
        console.error('Error loading team schools:', err);
        setTeamSchools([]);
      } finally {
        setLoadingTeamSchools(false);
      }
    };

    loadSchoolsForTeam();
    // Reset school selection when team changes
    setSelectedSchool(null);
  }, [selectedTeam?.id, enqueueSnackbar]);
  
  // Auto-select all grades when school changes
  useEffect(() => {
    if (selectedSchool && selectedGrades.length === 0) {
      setSelectedGrades([...GRADES]);
    }
  }, [selectedSchool, selectedGrades.length]);
  
  // Fetch stored summaries AND data for visualizations when school or drive time changes
  // Optimized: First check if stored summaries exist (fast), then fetch data only if needed
  useEffect(() => {
    const fetchStoredSummariesWithData = async () => {
      if (!selectedSchool?.uuid) {
        setGeographicSummary(null);
        setCompetitiveSummary(null);
        setProjectionsSummary(null);
        setStoredSummaryDates({});
        return;
      }
      
      setLoadingStored(true);
      try {
        // Step 1: Quick check if stored summaries exist (fast query)
        const storedResult = await getStoredSummariesAdmin(selectedSchool.uuid, driveTime);
        
        const driveTimeKey = String(driveTime);
        const hasStoredSummaries = storedResult.found && storedResult.summaries?.[driveTimeKey];
        const storedNarratives = hasStoredSummaries ? storedResult.summaries[driveTimeKey] : {};
        
        // If no stored summaries, clear state immediately and return (fast path)
        if (!hasStoredSummaries) {
          setGeographicSummary(null);
          setCompetitiveSummary(null);
          setProjectionsSummary(null);
          setCombinedSummary(null);
          setStoredSummaryDates({});
          setLoadingStored(false);
          return;
        }
        
        // Step 2: We have stored summaries - now fetch data for visualizations
        const dataResult = await getSummaryDataOnly(
          selectedSchool.uuid, 
          driveTime, 
          ['geographic', 'competitive', 'projections'], 
          selectedGrades, 
          governanceFilter
        );
        
        const dates = {};
        
        // Combine fresh data with stored narratives for Geographic
        if (storedNarratives.geographic) {
          setGeographicSummary({
            data: dataResult?.geographic || null,
            narrative: storedNarratives.geographic.full_narrative,
            debug: { parsed_sections: storedNarratives.geographic.sections },
            tokens_used: storedNarratives.geographic.tokens_used,
            from_cache: false,
            mode: 'stored',
            data_sources: dataResult?.data_sources
          });
          dates.geographic = storedNarratives.geographic.generated_at;
        } else {
          setGeographicSummary(null);
        }
        
        // Combine fresh data with stored narratives for Competitive
        if (storedNarratives.competitive) {
          setCompetitiveSummary({
            data: dataResult?.competitive || null,
            narrative: storedNarratives.competitive.full_narrative,
            debug: { parsed_sections: storedNarratives.competitive.sections },
            tokens_used: storedNarratives.competitive.tokens_used,
            from_cache: false,
            mode: 'stored',
            data_sources: dataResult?.data_sources
          });
          dates.competitive = storedNarratives.competitive.generated_at;
        } else {
          setCompetitiveSummary(null);
        }
        
        // Combine fresh data with stored narratives for Projections
        if (storedNarratives.projections) {
          setProjectionsSummary({
            data: dataResult?.projections || null,
            narrative: storedNarratives.projections.full_narrative,
            debug: { parsed_sections: storedNarratives.projections.sections },
            tokens_used: storedNarratives.projections.tokens_used,
            from_cache: false,
            mode: 'stored',
            data_sources: dataResult?.data_sources
          });
          dates.projections = storedNarratives.projections.generated_at;
        } else {
          setProjectionsSummary(null);
        }
        
        // Load Combined/Overview summary
        if (storedNarratives.combined) {
          setCombinedSummary({
            narrative: storedNarratives.combined.full_narrative,
            executive_summary: storedNarratives.combined.executive_summary,
            debug: { parsed_sections: storedNarratives.combined.sections },
            tokens_used: storedNarratives.combined.tokens_used,
            generated_at: storedNarratives.combined.generated_at,
            from_cache: false,
            mode: 'stored'
          });
          dates.combined = storedNarratives.combined.generated_at;
        } else {
          setCombinedSummary(null);
        }
        
        setStoredSummaryDates(dates);
        
      } catch (err) {
        console.error('Error fetching stored summaries with data:', err);
        setGeographicSummary(null);
        setCompetitiveSummary(null);
        setProjectionsSummary(null);
        setCombinedSummary(null);
        setStoredSummaryDates({});
      } finally {
        setLoadingStored(false);
      }
    };
    
    fetchStoredSummariesWithData();
  }, [selectedSchool?.uuid, driveTime, selectedGrades, governanceFilter]);
  
  // Load summary status when school is selected
  useEffect(() => {
    const loadSummaryStatus = async () => {
      if (!selectedSchool?.uuid) {
        setSummaryStatus(null);
        return;
      }
      
      setLoadingStatus(true);
      try {
        const status = await getSummaryStatus(selectedSchool.uuid);
        setSummaryStatus(status);
      } catch (err) {
        console.error('Error loading summary status:', err);
        setSummaryStatus(null);
      } finally {
        setLoadingStatus(false);
      }
    };
    
    loadSummaryStatus();
  }, [selectedSchool?.uuid]);
  
  const handleGenerateAll = async () => {
    if (!selectedSchool) {
      setError('Please select a school');
      return;
    }
    
    if (selectedGrades.length === 0) {
      setError('Please select at least one grade');
      return;
    }
    
    setError(null);
    
    // Generate all three summaries in parallel
    const promises = [
      generateGeographic(false),
      generateCompetitive(false),
      generateProjections(false)
    ];
    
    try {
      await Promise.all(promises);
      enqueueSnackbar('All summaries generated successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error generating summaries:', err);
      enqueueSnackbar('Some summaries failed to generate', { variant: 'warning' });
    }
  };
  
  const generateGeographic = async (showNotification = true) => {
    if (!selectedSchool) return;
    
    setLoadingGeographic(true);
    try {
      const result = await getGeographicSummary(
        selectedSchool.uuid,
        driveTime,
        selectedGrades,
        governanceFilter,
        schoolTypeFilter,
        false,
        verbosity,
        reasoningEffort
      );
      setGeographicSummary(result);
      if (showNotification) {
        enqueueSnackbar('Geographic summary generated', { variant: 'success' });
      }
    } catch (err) {
      console.error('Error generating geographic summary:', err);
      setGeographicSummary({ error: err.message });
      if (showNotification) {
        enqueueSnackbar(err.message || 'Failed to generate geographic summary', { variant: 'error' });
      }
    } finally {
      setLoadingGeographic(false);
    }
  };
  
  const generateCompetitive = async (showNotification = true) => {
    if (!selectedSchool) return;
    
    setLoadingCompetitive(true);
    try {
      const result = await getCompetitiveSummary(
        selectedSchool.uuid,
        driveTime,
        selectedGrades,
        governanceFilter,
        schoolTypeFilter,
        false,
        verbosity,
        reasoningEffort
      );
      setCompetitiveSummary(result);
      if (showNotification) {
        enqueueSnackbar('Competitive summary generated', { variant: 'success' });
      }
    } catch (err) {
      console.error('Error generating competitive summary:', err);
      setCompetitiveSummary({ error: err.message });
      if (showNotification) {
        enqueueSnackbar(err.message || 'Failed to generate competitive summary', { variant: 'error' });
      }
    } finally {
      setLoadingCompetitive(false);
    }
  };
  
  const generateProjections = async (showNotification = true) => {
    if (!selectedSchool) return;
    
    setLoadingProjections(true);
    try {
      const result = await getProjectionsSummary(
        selectedSchool.uuid,
        selectedGrades,
        false,
        verbosity,
        reasoningEffort
      );
      setProjectionsSummary(result);
      if (showNotification) {
        enqueueSnackbar('Projections summary generated', { variant: 'success' });
      }
    } catch (err) {
      console.error('Error generating projections summary:', err);
      setProjectionsSummary({ error: err.message });
      if (showNotification) {
        enqueueSnackbar(err.message || 'Failed to generate projections summary', { variant: 'error' });
      }
    } finally {
      setLoadingProjections(false);
    }
  };
  
  const handleExportClick = () => {
    if (!selectedSchool) {
      enqueueSnackbar('Please select a school', { variant: 'warning' });
      return;
    }
    
    // Check if at least one summary is generated
    if (!geographicSummary && !competitiveSummary && !projectionsSummary) {
      enqueueSnackbar('Please generate at least one summary before exporting', { variant: 'warning' });
      return;
    }
    
    setExportModalOpen(true);
  };
  
  /**
   * Handle viewing a school from the overview tab
   */
  const handleViewSchoolFromOverview = async (school) => {
    // Find the team for this school
    if (school.teams && school.teams.length > 0) {
      const teamName = school.teams[0];
      const team = teams.find(t => t.name === teamName);
      if (team) {
        setSelectedTeam(team);
        // Wait for team schools to load
        await new Promise(resolve => setTimeout(resolve, 100));
        // Find and select the school
        const foundSchool = teamSchools.find(s => s.uuid === school.uuid);
        if (foundSchool) {
          setSelectedSchool(foundSchool);
        }
      }
    }
    // Switch to Generate & View tab
    setMainTab(0);
    enqueueSnackbar(`Switched to ${school.name}`, { variant: 'info' });
  };

  const handleExportWithOptions = async (selectedSections, includeVisuals) => {
    setExporting(true);
    
    try {
      const originalTab = activeTab;
      let chartImages = {};
      
      if (includeVisuals) {
        // Capture charts from all selected sections
        chartImages = await captureAllChartImages(selectedSections);
      }
      
      // Restore original tab
      setActiveTab(originalTab);
      
      // Prepare data for export (null out unselected sections)
      const exportData = {
        geographic: selectedSections.geographic ? geographicSummary : null,
        competitive: selectedSections.competitive ? competitiveSummary : null,
        projections: selectedSections.projections ? projectionsSummary : null
      };
      
      // Export to HTML
      exportSummaryToHTML(
        selectedSchool.name,
        exportData.geographic,
        exportData.competitive,
        exportData.projections,
        {
          driveTime,
          grades: selectedGrades,
          governance: governanceFilter,
          schoolType: schoolTypeFilter
        },
        chartImages
      );
      
      enqueueSnackbar('Summary exported to HTML', { variant: 'success' });
      setExportModalOpen(false);
    } catch (err) {
      console.error('Error exporting to HTML:', err);
      enqueueSnackbar('Failed to export summary', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };
  
  /**
   * Save generated summaries to database using parallel approach
   * Generates all summaries concurrently for much faster processing
   */
  const handleSaveToDatabase = async () => {
    if (!selectedSchool) {
      enqueueSnackbar('Please select a school first', { variant: 'warning' });
      return;
    }
    
    setSaving(true);
    setGenerationProgress({ phase: 'starting', completedCount: 0, totalCount: 0 });
    
    const startTime = Date.now();
    
    try {
      // Generate drive times to process
      const driveTimes = generateAllDriveTimes ? [5, 10, 15] : [driveTime];
      
      // Use parallel generation with progress callback
      const result = await generateAllSummariesParallel(
        selectedSchool.uuid,
        driveTimes,
        (phase, completedCount, totalCount, currentResults) => {
          // Update progress state
          setGenerationProgress({ phase, completedCount, totalCount });
          
          // Show phase completion messages
          if (phase === 'phase1' && completedCount === totalCount) {
            const message = isDropAPinLocation
              ? `Generated ${completedCount} geographic ${completedCount === 1 ? 'summary' : 'summaries'}`
              : `Phase 1 complete: Generated ${completedCount} base summaries`;
            enqueueSnackbar(
              message,
              { variant: 'info', autoHideDuration: 3000 }
            );
          } else if (phase === 'phase2' && completedCount === totalCount) {
            enqueueSnackbar(
              `Phase 2 complete: Generated ${completedCount} overview summaries`,
              { variant: 'info', autoHideDuration: 3000 }
            );
          }
        },
        isDropAPinLocation
      );
      
      const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
      
      if (result.success) {
        const { successful, failed, total } = result.summary_count;
        const message = isDropAPinLocation
          ? `✓ Generated ${successful}/${total} geographic ${successful === 1 ? 'summary' : 'summaries'} in ${totalTime}s!`
          : `✓ Generated ${successful}/${total} summaries in ${totalTime}s!`;
        enqueueSnackbar(
          message,
          { variant: 'success', autoHideDuration: 5000 }
        );
        
        if (failed > 0) {
          enqueueSnackbar(
            `⚠ ${failed} summaries failed - check console for details`,
            { variant: 'warning', autoHideDuration: 5000 }
          );
        }
      } else {
        enqueueSnackbar(
          `Generation completed with errors after ${totalTime}s`,
          { variant: 'warning' }
        );
      }
      
      // Refresh status
      const status = await getSummaryStatus(selectedSchool.uuid);
      setSummaryStatus(status);
      
      // Fetch and display the saved summaries with fresh data for visualizations
      const [dataResult, storedResult] = await Promise.all([
        getSummaryDataOnly(selectedSchool.uuid, driveTime, ['geographic', 'competitive', 'projections'], selectedGrades, governanceFilter),
        getStoredSummariesAdmin(selectedSchool.uuid, driveTime)
      ]);
      
      const dtKey = String(driveTime);
      const storedNarratives = storedResult.found && storedResult.summaries?.[dtKey] 
        ? storedResult.summaries[dtKey] 
        : {};
      const dates = {};
      
      // Update Geographic with saved narrative + fresh data
      if (storedNarratives.geographic) {
        setGeographicSummary({
          data: dataResult?.geographic || null,
          narrative: storedNarratives.geographic.full_narrative,
          debug: { parsed_sections: storedNarratives.geographic.sections },
          tokens_used: storedNarratives.geographic.tokens_used,
          from_cache: false,
          mode: 'stored',
          data_sources: dataResult?.data_sources
        });
        dates.geographic = storedNarratives.geographic.generated_at;
      }
      
      // Update Competitive with saved narrative + fresh data
      if (storedNarratives.competitive) {
        setCompetitiveSummary({
          data: dataResult?.competitive || null,
          narrative: storedNarratives.competitive.full_narrative,
          debug: { parsed_sections: storedNarratives.competitive.sections },
          tokens_used: storedNarratives.competitive.tokens_used,
          from_cache: false,
          mode: 'stored',
          data_sources: dataResult?.data_sources
        });
        dates.competitive = storedNarratives.competitive.generated_at;
      }
      
      // Update Projections with saved narrative + fresh data
      if (storedNarratives.projections) {
        setProjectionsSummary({
          data: dataResult?.projections || null,
          narrative: storedNarratives.projections.full_narrative,
          debug: { parsed_sections: storedNarratives.projections.sections },
          tokens_used: storedNarratives.projections.tokens_used,
          from_cache: false,
          mode: 'stored',
          data_sources: dataResult?.data_sources
        });
        dates.projections = storedNarratives.projections.generated_at;
      }
      
      // Update Combined/Overview summary
      if (storedNarratives.combined) {
        setCombinedSummary({
          narrative: storedNarratives.combined.full_narrative,
          executive_summary: storedNarratives.combined.executive_summary,
          debug: { parsed_sections: storedNarratives.combined.sections },
          tokens_used: storedNarratives.combined.tokens_used,
          generated_at: storedNarratives.combined.generated_at,
          from_cache: false,
          mode: 'stored'
        });
        dates.combined = storedNarratives.combined.generated_at;
      }
      
      setStoredSummaryDates(dates);
      
    } catch (err) {
      console.error('Error saving summaries:', err);
      enqueueSnackbar(err.message || 'Failed to save summaries', { variant: 'error' });
    } finally {
      setSaving(false);
      setGenerationProgress(null);
    }
  };

  /**
   * Save the current preview to the database without regenerating
   */
  const handleSaveCurrentPreview = async () => {
    if (!selectedSchool) {
      enqueueSnackbar('Please select a school first', { variant: 'warning' });
      return;
    }
    
    // Check if we have any summaries to save
    const hasAnySummary = geographicSummary || competitiveSummary || projectionsSummary;
    if (!hasAnySummary) {
      enqueueSnackbar('No previews to save. Click "Preview" first to generate summaries.', { variant: 'warning' });
      return;
    }
    
    setSaving(true);
    try {
      // Build summaries object from current state
      // For drop-a-pin locations, only geographic summaries will be present
      const summariesToSave = {};
      
      if (geographicSummary?.narrative) {
        summariesToSave.geographic = {
          narrative: geographicSummary.narrative,
          tokens_used: geographicSummary.tokens_used || 0,
          debug: geographicSummary.debug
        };
      }
      
      // Competitive and projections will be empty for drop-a-pin locations
      if (competitiveSummary?.narrative && !isDropAPinLocation) {
        summariesToSave.competitive = {
          narrative: competitiveSummary.narrative,
          tokens_used: competitiveSummary.tokens_used || 0,
          debug: competitiveSummary.debug
        };
      }
      
      if (projectionsSummary?.narrative && !isDropAPinLocation) {
        summariesToSave.projections = {
          narrative: projectionsSummary.narrative,
          tokens_used: projectionsSummary.tokens_used || 0,
          debug: projectionsSummary.debug
        };
      }
      
      const result = await saveCurrentSummaries(
        selectedSchool.uuid,
        driveTime,
        summariesToSave
      );
      
      if (result.success) {
        const message = isDropAPinLocation 
          ? 'Geographic summary saved to database!' 
          : 'Current preview saved to database!';
        enqueueSnackbar(message, { variant: 'success' });
        
        // Refresh status
        const status = await getSummaryStatus(selectedSchool.uuid);
        setSummaryStatus(status);
        
        // Update stored dates
        const stored = await getStoredSummariesAdmin(selectedSchool.uuid, driveTime);
        const dtKey = String(driveTime);
        if (stored.found && stored.summaries?.[dtKey]) {
          const dtSummaries = stored.summaries[dtKey];
          const dates = {};
          
          if (dtSummaries.geographic) {
            dates.geographic = dtSummaries.geographic.generated_at;
          }
          if (dtSummaries.competitive) {
            dates.competitive = dtSummaries.competitive.generated_at;
          }
          if (dtSummaries.projections) {
            dates.projections = dtSummaries.projections.generated_at;
          }
          if (dtSummaries.combined) {
            dates.combined = dtSummaries.combined.generated_at;
          }
          setStoredSummaryDates(dates);
        }
      } else {
        enqueueSnackbar('Some summaries failed to save', { variant: 'warning' });
      }
    } catch (err) {
      console.error('Error saving current preview:', err);
      enqueueSnackbar(err.message || 'Failed to save preview', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Generate summaries for all schools in the selected team
   * Opens modal immediately, shows progress during generation, then displays results
   */
  const handleGenerateForTeam = async () => {
    if (!selectedTeam?.id) {
      enqueueSnackbar('Please select a team first', { variant: 'warning' });
      return;
    }

    const schoolCount = teamSchools?.length ?? 0;
    const confirmed = window.confirm(
      `This will generate AI summaries for ${schoolCount} school${schoolCount !== 1 ? 's' : ''} in "${selectedTeam.name}" for all drive times (5, 10, 15 min).\n\n` +
      `This may take several minutes. A progress modal will appear.\n\n` +
      `Continue?`
    );

    if (!confirmed) return;

    const startTime = Date.now();
    setGeneratingForTeam(true);
    setBatchProgressDialogOpen(true);
    setBatchProgress({
      isActive: true,
      totalSchools: schoolCount,
      completed: 0,
      failed: 0,
      verificationFailed: 0,
      currentBatch: 1,
      totalBatches: 1,
      startTime,
      avgTimePerSchool: 0,
      failedSchools: [],
      verificationFailedSchools: [],
      recentCompletions: [],
      mode: 'team'
    });

    try {
      const result = await generateForTeamStream(
        selectedTeam.id,
        true,
        { stop_on_verification_failure: stopOnVerificationFailure },
        (schoolResult) => {
          setBatchProgress(prev => {
            const isSuccess = schoolResult.success;
            const isVerificationFail = isSuccess && schoolResult.verification?.passed === false;
            const isFullyOk = isSuccess && schoolResult.verification?.passed !== false;
            const newCompleted = isFullyOk ? prev.completed + 1 : prev.completed;
            const newFailed = !isSuccess ? prev.failed + 1 : prev.failed;
            const newVerificationFailed = isVerificationFail ? prev.verificationFailed + 1 : prev.verificationFailed;
            const completions = isFullyOk
              ? [...(prev.recentCompletions || []), schoolResult]
              : prev.recentCompletions;
            const failedList = !isSuccess ? [...(prev.failedSchools || []), schoolResult] : prev.failedSchools;
            const verificationFailedList = isVerificationFail
              ? [...(prev.verificationFailedSchools || []), schoolResult]
              : prev.verificationFailedSchools;
            const totalProcessed = newCompleted + newFailed + newVerificationFailed;
            const avgTime = totalProcessed > 0 && startTime
              ? (Date.now() - startTime) / 1000 / totalProcessed
              : prev.avgTimePerSchool;
            const status = isFullyOk ? 'OK' : (!isSuccess ? 'Failed' : 'Quality issue');
            return {
              ...prev,
              completed: newCompleted,
              failed: newFailed,
              verificationFailed: newVerificationFailed,
              recentCompletions: completions,
              failedSchools: failedList,
              verificationFailedSchools: verificationFailedList,
              avgTimePerSchool: avgTime,
              lastProcessedSchool: { ...schoolResult, status }
            };
          });
        }
      );

      if (result?.success) {
        const results = result.results || [];
        const successful = results.filter(r => r.success && r.verification?.passed !== false);
        const verificationFailed = results.filter(r => r.success && r.verification?.passed === false);
        const failed = results.filter(r => !r.success);

        setBatchProgress(prev => ({
          ...prev,
          isActive: false,
          totalSchools: result.total_schools,
          completed: successful.length,
          failed: failed.length,
          verificationFailed: verificationFailed.length,
          failedSchools: failed,
          verificationFailedSchools: verificationFailed,
          recentCompletions: successful,
          avgTimePerSchool: result.avg_duration_per_school || prev.avgTimePerSchool,
          teamName: selectedTeam.name
        }));

        enqueueSnackbar(
          `Team "${selectedTeam.name}": ${successful.length} OK, ${verificationFailed.length} quality issues, ${failed.length} failed`,
          { variant: failed.length > 0 ? 'warning' : 'success' }
        );

        if (selectedSchool?.uuid) {
          const status = await getSummaryStatus(selectedSchool.uuid);
          setSummaryStatus(status);
        }
      } else {
        setBatchProgress(prev => ({
          ...prev,
          isActive: false,
          error: result?.error || 'Generation failed'
        }));
        enqueueSnackbar(result.error || 'Generate for team failed', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error generating for team:', err);
      setBatchProgress(prev => ({
        ...prev,
        isActive: false,
        error: err.message || 'Request failed'
      }));
      enqueueSnackbar(err.message || 'Failed to generate for team', { variant: 'error' });
    } finally {
      setGeneratingForTeam(false);
    }
  };

  /**
   * Generate summaries for all schools in EDC
   */
  const handleGenerateForAll = async (testLimit = null) => {
    // If testLimit is provided, use it for testing, otherwise process all
    const isTest = testLimit !== null;
    
    const locationScopeLabel =
      locationScopeForAll === 'drop_a_pin_only'
        ? 'Drop-a-pin only'
        : locationScopeForAll === 'non_drop_a_pin_only'
          ? 'Non-drop-a-pin only'
          : 'All schools';

    const confirmed = window.confirm(
      isTest
        ? `🧪 TEST MODE: Generate AI summaries for ${testLimit} schools\n\n` +
          `This is a test run to verify the batch generation system.\n\n` +
          `Expected time: ~${Math.ceil(testLimit / 5 * 4)}–${Math.ceil(testLimit / 5 * 5)} minutes\n\n` +
          `Continue?`
        : `⚠️ WARNING: This will generate AI summaries for ALL schools in EDC!\n\n` +
          `This is a LONG-RUNNING operation and will process schools in batches.\n\n` +
          `School scope: ${locationScopeLabel}\n` +
          `Batch size: ${batchSize} schools\n` +
          `Skip existing valid summaries: ${skipExistingForAll ? 'Yes' : 'No'}\n` +
          `Only stale/missing/invalid (>${staleDaysForAll} days): ${onlyStaleForAll ? 'Yes' : 'No'}\n` +
          `Stop on verification failure: ${stopOnVerificationFailure ? 'Yes' : 'No'}\n\n` +
          `The process now runs with 5x concurrency for faster completion.\n\n` +
          `Only proceed if you're sure you want to regenerate all summaries.\n\n` +
          `Continue?`
    );
    
    if (!confirmed) return;

    const startTime = Date.now();
    setGeneratingForAll(true);
    setBatchProgressDialogOpen(true);
    setBatchProgress({
      isActive: true,
      totalSchools: 0,
      completed: 0,
      failed: 0,
      verificationFailed: 0,
      currentBatch: 0,
      totalBatches: 0,
      startTime,
      avgTimePerSchool: 0,
      failedSchools: [],
      verificationFailedSchools: [],
      recentCompletions: [],
      mode: 'all'
    });

    let offset = 0;
    let totalSchools = 0;
    let totalBatches = 0;
    let totalProcessed = 0;
    let totalSuccessful = 0;
    let totalFailed = 0;
    let totalVerificationPassed = 0;
    let totalVerificationFailed = 0;
    let hasMore = true;
    let batchNumber = 0;
    let allFailedSchools = [];
    let allVerificationFailedSchools = [];
    let totalDuration = 0;
    let stoppedOnVerification = false;
    
    try {
      const effectiveBatchSize = isTest ? testLimit : batchSize;
      const batchOptions = {
        location_scope: locationScopeForAll,
        stop_on_verification_failure: stopOnVerificationFailure,
        ...(isTest
          ? {}
          : {
              skip_existing: skipExistingForAll,
              only_stale_days: onlyStaleForAll ? staleDaysForAll : null,
              include_quality_check: true
            })
      };

      const processBatch = async (batchOffset) => {
        return generateForAllStream(
          true,
          effectiveBatchSize,
          batchOffset,
          batchOptions,
          (batchStart) => {
            const total = batchStart.total_count || 0;
            const totalBatches = batchStart.total_batches || 1;
            const batchNum = batchStart.batch_number || 1;
            const schoolsForRun = isTest ? Math.min(testLimit, total) : total;
            const batchesForRun = isTest ? Math.ceil(schoolsForRun / effectiveBatchSize) : totalBatches;
            setBatchProgress(prev => ({
              ...prev,
              totalSchools: schoolsForRun,
              totalBatches: batchesForRun,
              currentBatch: batchNum
            }));
          },
          (schoolResult) => {
            setBatchProgress(prev => {
              const isSuccess = schoolResult.success;
              const isVerificationFail = isSuccess && schoolResult.verification?.passed === false;
              const isFullyOk = isSuccess && schoolResult.verification?.passed !== false;
              const newCompleted = isFullyOk ? prev.completed + 1 : prev.completed;
              const newFailed = !isSuccess ? prev.failed + 1 : prev.failed;
              const newVerificationFailed = isVerificationFail ? prev.verificationFailed + 1 : prev.verificationFailed;
              const completions = isFullyOk ? [...(prev.recentCompletions || []), schoolResult] : prev.recentCompletions;
              const failedList = !isSuccess ? [...(prev.failedSchools || []), schoolResult] : prev.failedSchools;
              const verificationFailedList = isVerificationFail
                ? [...(prev.verificationFailedSchools || []), schoolResult]
                : prev.verificationFailedSchools;
              const totalProcessed = newCompleted + newFailed + newVerificationFailed;
              const avgTime = totalProcessed > 0 && startTime
                ? (Date.now() - startTime) / 1000 / totalProcessed
                : prev.avgTimePerSchool;
              const schoolsLeft = prev.totalSchools - totalProcessed;
              const eta = schoolsLeft > 0 && avgTime > 0 ? schoolsLeft * avgTime / 5 : 0;
              return {
                ...prev,
                completed: newCompleted,
                failed: newFailed,
                verificationFailed: newVerificationFailed,
                recentCompletions: completions.slice(-20),
                failedSchools: failedList,
                verificationFailedSchools: verificationFailedList,
                avgTimePerSchool: avgTime,
                estimatedSecondsRemaining: eta,
                lastProcessedSchool: {
                  ...schoolResult,
                  status: isFullyOk ? 'OK' : (!isSuccess ? 'Failed' : 'Quality issue')
                }
              };
            });
          }
        );
      };

      let batchResult = await processBatch(0);
      if (!batchResult?.success) {
        throw new Error(batchResult?.error || 'Initial batch request failed');
      }

      totalSchools = isTest ? Math.min(testLimit, batchResult.total_in_system) : batchResult.total_in_system;
      totalBatches = Math.ceil(totalSchools / effectiveBatchSize);
      batchNumber = 1;
      totalProcessed += batchResult.processed_this_batch;
      totalSuccessful += batchResult.successful;
      totalFailed += batchResult.failed || 0;
      totalVerificationPassed += batchResult.verification_passed || 0;
      totalVerificationFailed += batchResult.verification_failed || 0;
      totalDuration += batchResult.batch_duration || 0;
      hasMore = !isTest && batchResult.has_more && totalProcessed < totalSchools;
      offset = batchResult.next_offset || effectiveBatchSize;

      const firstResults = batchResult.results || [];
      allFailedSchools.push(...firstResults.filter(r => !r.success));
      allVerificationFailedSchools.push(...firstResults.filter(r => r.success && r.verification?.passed === false));

      enqueueSnackbar(
        `Batch 1/${totalBatches}: ${batchResult.successful}/${batchResult.processed_this_batch} successful ` +
        `(${batchResult.avg_duration_per_school?.toFixed(1)}s avg)`,
        { variant: 'success', autoHideDuration: 3000 }
      );

      if (batchResult.stop_requested_due_to_verification) {
        stoppedOnVerification = true;
        hasMore = false;
        enqueueSnackbar(
          `Stopped after batch 1: ${batchResult.verification_failed || 0} verification failures.`,
          { variant: 'error', autoHideDuration: 10000 }
        );
      }

      while (hasMore && totalProcessed < totalSchools) {
        batchResult = await processBatch(offset);
        if (!batchResult?.success) {
          hasMore = false;
          break;
        }

        batchNumber += 1;
        totalProcessed += batchResult.processed_this_batch;
        totalSuccessful += batchResult.successful;
        totalFailed += batchResult.failed || 0;
        totalVerificationPassed += batchResult.verification_passed || 0;
        totalVerificationFailed += batchResult.verification_failed || 0;
        totalDuration += batchResult.batch_duration || 0;
        hasMore = !isTest && batchResult.has_more && totalProcessed < totalSchools;
        offset = batchResult.next_offset || offset + effectiveBatchSize;

        const batchResults = batchResult.results || [];
        allFailedSchools.push(...batchResults.filter(r => !r.success));
        allVerificationFailedSchools.push(...batchResults.filter(r => r.success && r.verification?.passed === false));

        const avgTime = totalDuration / totalSuccessful || 0;
        const completedNow = totalVerificationPassed;
        const failedNow = totalFailed;
        const verificationFailedNow = totalVerificationFailed;
        const batchNumNow = batchNumber;
        const hasMoreNow = hasMore;
        const totalProcessedNow = totalProcessed;
        setBatchProgress(prev => ({
          ...prev,
          completed: completedNow,
          failed: failedNow,
          verificationFailed: verificationFailedNow,
          currentBatch: batchNumNow,
          avgTimePerSchool: avgTime,
          failedSchools: allFailedSchools,
          verificationFailedSchools: allVerificationFailedSchools,
          recentCompletions: prev.recentCompletions?.slice(-20) || [],
          estimatedSecondsRemaining: hasMoreNow ? ((totalSchools - totalProcessedNow) * avgTime / 5) : 0
        }));

        enqueueSnackbar(
          `Batch ${batchNumber}/${totalBatches}: ${batchResult.successful}/${batchResult.processed_this_batch} successful. ` +
          `Total: ${totalSuccessful}/${totalSchools} (${((totalSuccessful / totalSchools) * 100).toFixed(1)}%)`,
          { variant: 'success', autoHideDuration: 3000 }
        );

        if (batchResult.stop_requested_due_to_verification) {
          stoppedOnVerification = true;
          hasMore = false;
          enqueueSnackbar(
            `Stopped at batch ${batchNumber}: ${batchResult.verification_failed || 0} verification failures.`,
            { variant: 'error', autoHideDuration: 10000 }
          );
          break;
        }
      }
      
      // Final summary
      const finalElapsedSeconds = (Date.now() - startTime) / 1000;
      const finalElapsedMinutes = (finalElapsedSeconds / 60).toFixed(1);
      
      if (stoppedOnVerification) {
        enqueueSnackbar(
          `⛔ Stopped due to verification failures. ${totalSuccessful}/${totalProcessed} successful, ` +
          `${totalVerificationFailed} quality-failed. Time: ${finalElapsedMinutes} minutes`,
          { variant: 'error', autoHideDuration: 12000 }
        );
      } else {
        enqueueSnackbar(
          `✅ Completed! ${totalSuccessful}/${totalProcessed} successful, ${totalFailed} failed, ` +
          `${totalVerificationPassed} quality-passed, ${totalVerificationFailed} quality-failed. ` +
          `Time: ${finalElapsedMinutes} minutes`,
          { variant: 'success', autoHideDuration: 10000 }
        );
      }
      
      if (allFailedSchools.length > 0) {
        enqueueSnackbar(
          `⚠️ ${allFailedSchools.length} schools failed. Check console for details.`,
          { variant: 'warning', autoHideDuration: 10000 }
        );
        console.log('Failed schools:', allFailedSchools);
      }
    } catch (err) {
      console.error('Error generating for all:', err);
      enqueueSnackbar(err.message || 'Failed to generate for all schools', { variant: 'error' });
    } finally {
      setGeneratingForAll(false);
      setBatchProgress(prev => ({
        ...prev,
        isActive: false,
        completed: totalVerificationPassed,
        failed: totalFailed,
        verificationFailed: totalVerificationFailed,
        failedSchools: allFailedSchools,
        verificationFailedSchools: allVerificationFailedSchools,
        currentBatch: batchNumber
      }));
    }
  };

  /**
   * Capture charts from all selected sections
   */
  const captureAllChartImages = async (selectedSections) => {
    const images = {};
    const originalTab = activeTab;
    
    // Capture charts from each selected section
    const sectionsToCapture = [];
    if (selectedSections.geographic) sectionsToCapture.push({ tab: 0, name: 'Geographic' });
    if (selectedSections.competitive) sectionsToCapture.push({ tab: 1, name: 'Competitive' });
    if (selectedSections.projections) sectionsToCapture.push({ tab: 2, name: 'Projections' });
    
    for (const section of sectionsToCapture) {
      console.log(`Capturing charts from ${section.name} tab...`);
      
      // Switch to the tab
      setActiveTab(section.tab);
      
      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Capture charts
      const sectionImages = await captureChartImages();
      
      // Merge images
      Object.assign(images, sectionImages);
    }
    
    // Restore original tab
    setActiveTab(originalTab);
    
    return images;
  };
  
  /**
   * Capture chart images as base64 for HTML export
   */
  const captureChartImages = async () => {
    const images = {};
    
    // Give charts time to fully render
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Find all canvas elements rendered by Chart.js
    const canvases = document.querySelectorAll('canvas');
    
    console.log(`Found ${canvases.length} canvas elements on current tab`);
    
    canvases.forEach((canvas, index) => {
      try {
        const dataUrl = canvas.toDataURL('image/png', 1.0);
        
        // Try multiple methods to identify the chart
        let chartTitle = null;
        
        // Method 1: Look for MUI Card header with h6
        const cardContent = canvas.closest('[class*="MuiCardContent"]');
        if (cardContent) {
          const h6 = cardContent.querySelector('h6');
          if (h6) chartTitle = h6.textContent;
        }
        
        // Method 2: Look in parent structure
        if (!chartTitle) {
          const parent = canvas.parentElement?.parentElement?.parentElement;
          const h6 = parent?.querySelector('h6');
          if (h6) chartTitle = h6.textContent;
        }
        
        console.log(`  Canvas ${index}: Title="${chartTitle}"`);
        
        // Identify chart type based on title
        if (chartTitle?.includes('Population Trends')) {
          images.populationTrends = dataUrl;
          console.log('  ✓ Captured Population Trends chart');
        } else if (chartTitle?.includes('Demographics')) {
          images.demographics = dataUrl;
          console.log('  ✓ Captured Demographics chart');
        } else if (chartTitle?.includes('Enrollment Projections') || chartTitle?.includes('5-Year')) {
          images.enrollmentProjections = dataUrl;
          console.log('  ✓ Captured Enrollment Projections chart');
        } else if (chartTitle?.includes('Market Share')) {
          images.marketShare = dataUrl;
          console.log('  ✓ Captured Market Share chart');
        } else if (chartTitle?.includes('Focus Schools') || chartTitle?.includes('Competitor')) {
          images.focusSchools = dataUrl;
          console.log('  ✓ Captured Focus Schools chart');
        } else {
          // Generic capture with index
          const key = `chart_${index}`;
          images[key] = dataUrl;
          console.log(`  ✓ Captured generic chart as ${key}`);
        }
      } catch (error) {
        console.error(`Failed to capture canvas ${index}:`, error);
      }
    });
    
    console.log('Total chart images captured:', Object.keys(images));
    return images;
  };
  
  const isGenerating = loadingGeographic || loadingCompetitive || loadingProjections;
  const batchGenerationSection = (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
        Batch Generation (Admin) - System-Wide Operations
      </Typography>
      <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          ⚠️ These operations are INDEPENDENT of the school selected below
        </Typography>
        <Typography variant="caption" component="div">
          • They query ALL active schools directly from the database<br/>
          • Test Mode processes the first 10 schools found (ordered by UUID)<br/>
          • Full Mode processes all ~1700 schools in 250-sized batches by default<br/>
          • Use "Generate for Team" to process only schools in the selected team below
        </Typography>
      </Alert>
      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 1 }}>
          Full Run Filters
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>School Scope</InputLabel>
            <Select
              value={locationScopeForAll}
              onChange={(e) => setLocationScopeForAll(e.target.value)}
              label="School Scope"
              disabled={generatingForAll}
            >
              <MenuItem value="all">All schools</MenuItem>
              <MenuItem value="non_drop_a_pin_only">Non-drop-a-pin only</MenuItem>
              <MenuItem value="drop_a_pin_only">Drop-a-pin only</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Batch Size"
            type="number"
            size="small"
            value={batchSize}
            onChange={(e) => setBatchSize(Math.min(250, Math.max(1, Number(e.target.value) || 1)))}
            sx={{ width: 120 }}
            inputProps={{ min: 1, max: 250 }}
            disabled={generatingForAll}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              id="skipExistingForAll"
              checked={skipExistingForAll}
              onChange={(e) => setSkipExistingForAll(e.target.checked)}
              disabled={generatingForAll}
            />
            <label htmlFor="skipExistingForAll">
              <Typography variant="body2" color="text.secondary">
                Skip existing valid summaries
              </Typography>
            </label>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              id="onlyStaleForAll"
              checked={onlyStaleForAll}
              onChange={(e) => setOnlyStaleForAll(e.target.checked)}
              disabled={generatingForAll}
            />
            <label htmlFor="onlyStaleForAll">
              <Typography variant="body2" color="text.secondary">
                Only stale/missing/invalid
              </Typography>
            </label>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              id="stopOnVerificationFailure"
              checked={stopOnVerificationFailure}
              onChange={(e) => setStopOnVerificationFailure(e.target.checked)}
              disabled={generatingForAll}
            />
            <label htmlFor="stopOnVerificationFailure">
              <Typography variant="body2" color="text.secondary">
                Stop if verification fails
              </Typography>
            </label>
          </Box>
          <TextField
            label="Stale Days"
            type="number"
            size="small"
            value={staleDaysForAll}
            onChange={(e) => setStaleDaysForAll(Math.max(1, Number(e.target.value) || 1))}
            sx={{ width: 120 }}
            inputProps={{ min: 1 }}
            disabled={generatingForAll || !onlyStaleForAll}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleGenerateForTeam}
          disabled={!selectedTeam || generatingForTeam || generatingForAll}
          startIcon={generatingForTeam ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ flex: '1 1 200px' }}
        >
          {generatingForTeam ? 'Generating...' : `Generate for "${selectedTeam?.name || 'Team'}"`}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="info"
          onClick={() => handleGenerateForAll(10)}
          disabled={generatingForTeam || generatingForAll}
          startIcon={generatingForAll ? <CircularProgress size={16} /> : <AssistantIcon />}
          sx={{ flex: '1 1 200px' }}
        >
          🧪 Test Mode (10 Schools)
        </Button>
        <Button
          variant="contained"
          size="small"
          color="warning"
          onClick={() => handleGenerateForAll(null)}
          disabled={generatingForTeam || generatingForAll}
          startIcon={generatingForAll ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ flex: '1 1 200px' }}
        >
          {generatingForAll ? 'Generating...' : '⚠️ All Schools (~1700)'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        💡 Tip: Start with "Test Mode" to verify the system before running the full batch
      </Typography>
    </Paper>
  );

  return (
    <PageLayout 
      title="AI School Summary"
      icon={<AssistantIcon />}
    >
      {/* Top-Level Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={mainTab} 
          onChange={(e, newValue) => setMainTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Generate & View" icon={<SummarizeIcon />} iconPosition="start" />
          <Tab label="Overview & Metrics" icon={<BarChartIcon />} iconPosition="start" />
          <Tab label="Quality Report" icon={<AssistantIcon />} iconPosition="start" />
          <Tab label="Multi-School" icon={<GroupsIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 1: Generate & View */}
      {mainTab === 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setPromptEditorOpen(true)}
            >
              Edit Prompts
            </Button>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Generate comprehensive AI-powered summaries covering geographic context, competitive landscape, and enrollment projections.
            Summaries are based on 10-minute drive time data by default and can be customized with filters.
          </Alert>
          
          {batchGenerationSection}

        {/* School Selection Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon /> School Selection
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedTeam}
                onChange={(event, newValue) => setSelectedTeam(newValue)}
                options={teams}
                getOptionLabel={(option) => option.name || ''}
                loading={loadingTeams}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Team"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTeams ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedSchool}
                onChange={(event, newValue) => setSelectedSchool(newValue)}
                options={teamSchools}
                getOptionLabel={(option) => option.name || ''}
                loading={loadingTeamSchools}
                disabled={!selectedTeam}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select School"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTeamSchools ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Status Indicator */}
        {selectedSchool && summaryStatus && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: summaryStatus.has_any_summaries ? 'success.50' : 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {summaryStatus.has_any_summaries ? (
                <CheckCircleIcon color="success" />
              ) : (
                <CancelIcon color="disabled" />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {summaryStatus.has_any_summaries ? 'Stored Summaries Available' : 'No Stored Summaries'}
                </Typography>
                {summaryStatus.has_any_summaries && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    {Object.entries(summaryStatus.status || {}).flatMap(([mode, timesMap]) =>
                      Object.entries(timesMap || {}).map(([dt, typesMap]) => {
                        const availableTypes = Object.keys(typesMap || {}).filter(k => typesMap[k]?.available);
                        return availableTypes.length > 0 ? (
                          <Chip
                            key={`${mode}-${dt}`}
                            label={`${mode === 'walk' ? 'Walk' : 'Drive'} ${dt}min: ${availableTypes.join(', ')}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ) : null;
                      })
                    )}
                  </Box>
                )}
              </Box>
              {loadingStatus && <CircularProgress size={20} />}
            </Box>
          </Paper>
        )}

        {/* Drop-a-Pin Location Alert */}
        {selectedSchool && isDropAPinLocation && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            icon={<RoomIcon />}
          >
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Geographic Location (Drop-a-Pin)
            </Typography>
            <Typography variant="body2">
              This is a prospective location for potential school development without enrollment data. 
              Only <strong>geographic summaries</strong> can be generated for this location.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Drop-a-pin geographic summaries focus on <strong>market opportunity</strong> and highlight a range of nearby schools across different grade levels (Elementary, Middle, High), 
              including declining enrollment percentages to identify market gaps.
            </Typography>
          </Alert>
        )}

        {/* Parameters Section - Compact */}
        {selectedSchool && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Drive Time</InputLabel>
                  <Select
                    value={driveTime}
                    onChange={(e) => setDriveTime(e.target.value)}
                    label="Drive Time"
                  >
                    {DRIVE_TIMES.map(time => (
                      <MenuItem key={time} value={time}>{time} minutes</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleGenerateAll}
                    disabled={isGenerating || !selectedSchool}
                    startIcon={isGenerating ? <CircularProgress size={20} /> : <SummarizeIcon />}
                    title="Regenerate summaries locally (does not save to database)"
                  >
                    {isGenerating ? 'Generating...' : 'Preview'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveCurrentPreview}
                    disabled={saving || isGenerating || !selectedSchool || (!geographicSummary && !competitiveSummary && !projectionsSummary)}
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    title="Save current preview to database (exactly what you see)"
                  >
                    {saving ? 'Saving...' : 'Save Current'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={handleSaveToDatabase}
                    disabled={saving || isGenerating || !selectedSchool}
                    startIcon={saving ? <CircularProgress size={20} /> : <SummarizeIcon />}
                    title={isDropAPinLocation ? "Generate and save geographic summaries for all selected drive times" : "Regenerate NEW summaries and save to database (memory-efficient)"}
                  >
                    {saving && generationProgress 
                      ? generationProgress.phase === 'phase1'
                        ? isDropAPinLocation
                          ? `Generating geographic (${generationProgress.completedCount}/${generationProgress.totalCount})...`
                          : `Phase 1: Generating summaries (${generationProgress.completedCount}/${generationProgress.totalCount})...`
                        : generationProgress.phase === 'phase2'
                          ? `Phase 2: Generating overviews (${generationProgress.completedCount}/${generationProgress.totalCount})...`
                          : 'Starting...'
                      : saving 
                        ? 'Saving...' 
                        : isDropAPinLocation ? 'Generate & Save Geographic' : 'Regenerate & Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleExportClick}
                    disabled={!geographicSummary && !competitiveSummary && !projectionsSummary}
                    sx={{ minWidth: 'auto', px: 2 }}
                    title="Export current summaries to HTML"
                  >
                    <DownloadIcon />
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="right">
                  Verbosity: {verbosity.charAt(0).toUpperCase() + verbosity.slice(1)} | 
                  Reasoning: {reasoningEffort.charAt(0).toUpperCase() + reasoningEffort.slice(1)}
                </Typography>
              </Grid>
            </Grid>
            
            {/* Database Status & Dates */}
            <Box sx={{ mt: 2 }}>
              {loadingStored ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading data and stored summaries...
                  </Typography>
                </Box>
              ) : Object.keys(storedSummaryDates).length > 0 ? (
                <Box sx={{ mb: 1, p: 1, backgroundColor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.dark" fontWeight="medium">
                    ✅ Loaded: Fresh data + stored AI narratives for {driveTime}-minute drive time
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    {storedSummaryDates.geographic && (
                      <Typography variant="caption" color="text.secondary">
                        Geographic: {new Date(storedSummaryDates.geographic).toLocaleDateString()} {new Date(storedSummaryDates.geographic).toLocaleTimeString()}
                      </Typography>
                    )}
                    {storedSummaryDates.competitive && (
                      <Typography variant="caption" color="text.secondary">
                        Competitive: {new Date(storedSummaryDates.competitive).toLocaleDateString()} {new Date(storedSummaryDates.competitive).toLocaleTimeString()}
                      </Typography>
                    )}
                    {storedSummaryDates.projections && (
                      <Typography variant="caption" color="text.secondary">
                        Projections: {new Date(storedSummaryDates.projections).toLocaleDateString()} {new Date(storedSummaryDates.projections).toLocaleTimeString()}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                  ⚠️ No AI narratives stored for {driveTime}-minute drive time. Click "Regenerate & Save" to generate and store.
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="checkbox"
                  id="generateAllDriveTimes"
                  checked={generateAllDriveTimes}
                  onChange={(e) => setGenerateAllDriveTimes(e.target.checked)}
                />
                <label htmlFor="generateAllDriveTimes">
                  <Typography variant="body2" color="text.secondary">
                    Generate for all drive times (5, 10, 15 min) when saving
                  </Typography>
                </label>
              </Box>
            </Box>
            
            {/* Advanced Parameters Accordion */}
            <Accordion 
              expanded={advancedExpanded} 
              onChange={() => setAdvancedExpanded(!advancedExpanded)}
              sx={{ mt: 2, boxShadow: 'none', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon fontSize="small" />
                  <Typography variant="body2" fontWeight="medium">Advanced Parameters</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      size="small"
                      value={selectedGrades}
                      onChange={(event, newValue) => setSelectedGrades(newValue)}
                      options={GRADES}
                      renderInput={(params) => (
                        <TextField {...params} label="Grade Filters" variant="outlined" />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip key={key} label={option} size="small" {...tagProps} />
                          );
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      size="small"
                      value={governanceFilter}
                      onChange={(event, newValue) => setGovernanceFilter(newValue)}
                      options={GOVERNANCE_OPTIONS}
                      renderInput={(params) => (
                        <TextField {...params} label="Governance Filters" variant="outlined" />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip key={key} label={option} size="small" {...tagProps} />
                          );
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Verbosity</InputLabel>
                      <Select
                        value={verbosity}
                        onChange={(e) => setVerbosity(e.target.value)}
                        label="Verbosity"
                      >
                        <MenuItem value="low">Low (Terse - 2 paragraphs)</MenuItem>
                        <MenuItem value="medium">Medium (Balanced - 3-4 paragraphs)</MenuItem>
                        <MenuItem value="high">High (Detailed - 5-6 paragraphs)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Reasoning Effort</InputLabel>
                      <Select
                        value={reasoningEffort}
                        onChange={(e) => setReasoningEffort(e.target.value)}
                        label="Reasoning Effort"
                      >
                        <MenuItem value="minimal">Minimal (Fast, simpler analysis)</MenuItem>
                        <MenuItem value="medium">Medium (Balanced speed & depth)</MenuItem>
                        <MenuItem value="high">High (Thorough, slower analysis)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs Section */}
        {selectedSchool && (
          <Paper sx={{ mt: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab 
                icon={<AssistantIcon />} 
                label="Overview" 
                iconPosition="start"
                disabled={isDropAPinLocation}
              />
              <Tab 
                icon={<PublicIcon />} 
                label="Geographic" 
                iconPosition="start"
              />
              <Tab 
                icon={<CompetitiveIcon />} 
                label="Competitive" 
                iconPosition="start"
                disabled={isDropAPinLocation}
              />
              <Tab 
                icon={<TrendingUpIcon />} 
                label="Projections" 
                iconPosition="start"
                disabled={isDropAPinLocation}
              />
            </Tabs>
            <Divider />
            <Box sx={{ p: 3 }}>
              {/* Overview Tab - Combined Summary */}
              {activeTab === 0 && (
                <Box>
                  {loadingStored ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                      <CircularProgress sx={{ mr: 2 }} />
                      <Typography>Generating combined overview...</Typography>
                    </Box>
                  ) : combinedSummary ? (
                    <Box>
                      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <AssistantIcon color="primary" />
                        Executive Overview for {selectedSchool.name}
                      </Typography>
                      
                      <FormattedNarrative 
                        narrative={combinedSummary.narrative || combinedSummary.full_narrative || combinedSummary.executive_summary || ''}
                        parsedSections={combinedSummary.debug?.parsed_sections}
                        excludeSections={['Executive Summary']}
                        hideExecutiveSummary={true}
                      />
                      
                      {combinedSummary.generated_at && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Generated: {new Date(combinedSummary.generated_at).toLocaleString()}
                          </Typography>
                          {combinedSummary.tokens_used > 0 && (
                            <Chip 
                              label={`${combinedSummary.tokens_used} tokens`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}
                      
                      {combinedSummary.mode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Mode: {combinedSummary.mode === 'stored' ? 'From Database' : combinedSummary.mode}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <AssistantIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No combined overview available
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        The combined overview is generated automatically when you use "Regenerate &amp; Save All".
                        It synthesizes the Geographic, Competitive, and Projections summaries into a single executive overview.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Geographic Tab */}
              {activeTab === 1 && (
                <GeographicSummaryTab
                  summary={geographicSummary}
                  loading={loadingGeographic}
                  onGenerate={() => generateGeographic(true)}
                  onRegenerate={() => generateGeographic(true)}
                  schoolName={selectedSchool.name}
                />
              )}
              {activeTab === 2 && (
                <CompetitiveSummaryTab
                  summary={competitiveSummary}
                  loading={loadingCompetitive}
                  onGenerate={() => generateCompetitive(true)}
                  onRegenerate={() => generateCompetitive(true)}
                  schoolName={selectedSchool.name}
                />
              )}
              {activeTab === 3 && (
                <ProjectionsSummaryTab
                  summary={projectionsSummary}
                  loading={loadingProjections}
                  onGenerate={() => generateProjections(true)}
                  onRegenerate={() => generateProjections(true)}
                  schoolName={selectedSchool.name}
                  selectedSchool={selectedSchool}
                />
              )}
            </Box>
          </Paper>
        )}

        {!selectedSchool && !loadingTeams && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SchoolIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Select a school to begin
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a team and school to generate comprehensive AI-powered summaries
            </Typography>
          </Box>
        )}
        </Box>
      )}

      {/* Tab 2: Overview & Metrics */}
      {mainTab === 1 && (
        <Box sx={{ mb: 4 }}>
          <SummaryOverview onViewSchool={handleViewSchoolFromOverview} />
        </Box>
      )}
      
      {/* Tab 3: Quality Report */}
      {mainTab === 2 && (
        <Box sx={{ mb: 4 }}>
          <QualityReportTab />
        </Box>
      )}

      {/* Tab 4: Multi-School Rollup Summaries */}
      {mainTab === 3 && (
        <Box sx={{ mb: 4 }}>
          <MultiSchoolSummaryTab />
        </Box>
      )}
      
      {/* Prompt Editor Modal */}
      <PromptEditor 
        open={promptEditorOpen}
        onClose={() => setPromptEditorOpen(false)}
      />
      
      {/* Export Modal */}
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExportWithOptions}
        availableSections={{
          geographic: !!geographicSummary,
          competitive: !!competitiveSummary,
          projections: !!projectionsSummary
        }}
        exporting={exporting}
      />
      
      {/* Batch Progress Dialog */}
      <BatchProgressDialog
        progress={batchProgress}
        open={batchProgressDialogOpen}
        onClose={() => setBatchProgressDialogOpen(false)}
      />
    </PageLayout>
  );
};

export default AISummary;

